/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { parseDefaultHtml } from "./createLoginPage/loginPageSubsteps/PostLoginPageSubstep";
import getCreateLoginTemplates from "./createLoginPage/CreateLoginTemplates";
import debug from "../../util/constants/Debug";

export const getElementIndex = (steps, stepIndex, substepIndex) => {
  if (steps == null || stepIndex == null || substepIndex == null) {
    return null;
  }  

  // helper to get element count in substep up to optional index
  const getSubstepElements = (substeps, index = null) => {
    return substeps.reduce((acc, substep, i) => {
      return (index == null || i < index) && substep.type === "element" ? acc + 1 : acc;
    }, 0);
  };
  
  // get previous step element count
  const prevStepElements = steps.reduce((acc, step, i) => {
    return acc + (i < stepIndex ? getSubstepElements(step.substeps) : 0);
  }, 0);

  // get current step element count
  const currentStepElements = getSubstepElements(steps[stepIndex].substeps, substepIndex);

  // return total
  return prevStepElements + currentStepElements;
};

export const convertPostLoginType = (type) => {
  switch (type) {
    // cloud to app
    case "facebook_like": return "facebook";
    case "instagram_follow": return "instagram";
    case "google_review": return "google";
    case "default_page": return "default";
    case "embedded_video": return "video";
    case "custom_message": return "html";
    case "none": return null;
    // app to cloud
    case "facebook": return "facebook_like";
    case "instagram": return "instagram_follow";
    case "google": return "google_review";
    case "default": return "default_page";
    case "video": return "embedded_video";
    case "html": return "custom_message";
    default: return "none";
  }
};

export const parseInitialLayout = (layout, setValue, forceName = null, hasSmsConfig = true,  hasFacebookConfig = true, hasGoogleConfig = true, hasTwitterConfig = true,) => {
  // nothing to do if no layout
  if (!layout) {
    return;
  }

  // set element config
  const layoutConfig = layout.config ?? {};
  const elements = [ ...layoutConfig.foreground, ...layoutConfig.framing ];

  let elementConfig = [];
  let showLanguageBar = false;

  getCreateLoginTemplates().steps.forEach(step => {
    step.substeps.forEach(substep => {
      // ignore non-element type steps
      if (substep.type !== "element") {
        return;
      }

      // find config based on type
      const type = substep.config.element;
      const config = elements.find(element => element.type === type)?.config;
      if (type === "loginFields" && config?.loginMethod?.methods) {
        config.loginMethod.methods = config.loginMethod.methods.filter((i) => {
          if (i.type === "sms") { 
            return hasSmsConfig;
          } else if (i.type === "facebook") {
              return hasFacebookConfig
          } else if (i.type === "google") {
              return hasGoogleConfig
          } else if (i.type === "twitter") {
              return hasTwitterConfig
          } else {
            return true;
          }
        }) 
      }
      elementConfig.push({ ...config });

      // check to show language bar
      if (type === "localizationBar") {
        showLanguageBar = config?.visible ?? false;
      }
    });
  });

  // parse out fields
  const config = layout.config ?? {};
  const tos = layout.tos ?? {};
  const postLogin = layout.postLogin ?? {};
  const postLoginDefault = parseDefaultHtml(postLogin.default);
  const redirect = layout.redirect ?? {};
  const verify = layout.verify ?? {};
  const gdpr = layout.gdpr ?? {};
  const forceGdpr = layout.forceGdpr ?? false;
  const splashScreen = layout.splashScreen ?? {};
  
  const valueList = [
    { key: "id", value: layout.id },
    { key: "name", value: forceName ?? layout.name },
    { key: "element", value: elementConfig },
    { key: "background", value: config.background },
    { key: "termsAndConditions.custom", value: tos.custom },
    { key: "postLogin.type", value: postLogin.type },
    { key: "postLogin.facebookUrl", value: postLogin.facebookUrl },
    { key: "postLogin.instagramProfile", value: postLogin.instagramProfile },
    ...((postLoginDefault != null) ? [
      { key: "postLogin.default", value: postLogin.default },
      { key: "postLogin.defaultTitle", value: postLoginDefault.title },
      { key: "postLogin.defaultBody", value: postLoginDefault.body },
      { key: "postLogin.defaultImage", value: postLoginDefault.image },
      { key: "postLogin.defaultTitleSize", value: postLoginDefault.titleSize },
      { key: "postLogin.defaultBodySize", value: postLoginDefault.bodySize },
      { key: "postLogin.defaultFont", value: postLoginDefault.font },
      { key: "postLogin.defaultFontColor", value: postLoginDefault.fontColor },
    ] : []),
    { key: "postLogin.videoUrl", value: postLogin.videoUrl },
    { key: "postLogin.html", value: postLogin.html },
    { key: "redirect.enabled", value: redirect.enabled },
    { key: "redirect.delay", value: redirect.delay },
    { key: "redirect.redirectUrl", value: redirect.redirectUrl },
    { key: "localization.default", value: layout.localization },
    { key: "localization.showBar", value: showLanguageBar },
    { key: "verification.emailVerify", value: verify.email },
    { key: "verification.smsVerify", value: verify.sms },
    { key: "gdpr.enabled", value: forceGdpr || gdpr.enabled },
    { key: "gdpr.form", value: gdpr.form },
    { key: "gdpr.formEnabled", value: forceGdpr || gdpr.enabled || gdpr.formEnabled },
    { key: "preLoginVideo.setId", value: splashScreen.id },
    { key: "preLoginVideo.size", value: splashScreen.size },
    { key: "preLoginVideo.name", value: splashScreen.name },
    { key: "preLoginVideo.enabled", value: splashScreen.enabled },
  ];  

  // set values
  valueList.forEach(item => setValue(item.key, item.value));
};

export const parseCreateLoginPageContext = (context, form) => {
  // parse login page config
  let config = null;
  
  const background = form.background;
  const elementForm = form.element;
  const template = { ...context.template, ...form.template };
  const elementConfig = template.steps?.map((step) => {
    return step.substeps?.filter((substep) => substep.type === "element") ?? [];
  })?.flat();

  const foreground = [];
  const framing = [];

  elementConfig?.forEach((item, index) => {
    // validate
    const config = item.config;
    if (config == null) {
      return;
    }

    // get layer element is apart of
    let list;
    switch (config.layer) {
      case "foreground": list = foreground; break;
      case "framing": list = framing; break;
      default:
        debug.log("Invalid layer: " + config.layer);
        return;
    }

    // get associated form data
    const formData = elementForm?.[index] ?? {};

    // construct encoding
    list.push({
      config: formData,
      type: config.element,
      ...config.dimension
    });
  });

  config = { background, foreground, framing };

  // parse post-login data
  const postLogin = form.postLogin ?? {};
  const redirect = form.redirect ?? {};

  const postLoginConfig = {
    ...postLogin,
    postLoginEnabled: postLogin.type && postLogin.type !== "none",
    ...redirect,
    redirectEnabled: redirect.enabled,
    delay: parseInt(redirect.delay),
  };

  // get previously saved id
  const id = form.id;

  // get name
  const name = form.name ?? "New Layout";

  // localization
  const localization = form.localization?.default ?? "";

  // terms of service
  const tos = {
    default: !form.termsAndConditions?.custom,
    custom: form.termsAndConditions?.custom
  };

  // email and sms verification
  const verify = {
    email: form.verification?.emailVerify,
    sms: form.verification?.smsVerify,
  };

  // GDPR compliance
  const gdpr = {
    enabled: form.gdpr?.enabled,
    form: form.gdpr?.form,
    formEnabled: form.gdpr?.formEnabled
  };

  // splash screen
  const splashScreen = {
    newFile: form.preLoginVideo?.file?.[0],
    setId: form.preLoginVideo?.setId,
    enabled: form.preLoginVideo?.enabled ?? false
  };

  // return info
  return { id, name, config, postLogin: postLoginConfig, localization, verify, tos, gdpr, splashScreen };
};
